import { type ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import { codeCoverageRouteDefinitions } from '@/features/codeCoverage/routes/routeDefinitions';
import { ProjectSettingsPage, ProjectSettingsSlugRedirect } from '@/features/projects/routes';
import {
  BuildsIcon,
  DriveFileMoveIcon,
  FeaturesIcon,
  PlayCircleFilledWhiteOutlinedIcon,
  ReleasesIcon,
  SettingsIcon,
  StagesIcon,
  SuitesIcon,
  TestsIcon,
} from '@/ui/primitives';
import { AUTH_ROUTE_PATHS } from './authRoutePaths';
import { LegacyWorkspaceSettingsRedirect } from './LegacyWorkspaceSettingsRedirect';
import { projectScopedProductRouteDefinitions } from './projectScopedProductRoutes';
import { publicSiteRouteDefinitions } from './publicSiteRoutes';
import {
  AcceptInvitationPage,
  AuthDebugIndexPage,
  AuthDebugScreenPage,
  AuthCallbackPage,
  BuildDetailPage,
  ExecutionRunDetailPage,
  ExecutionRunsPage,
  ExecutionRunsTableDebugPage,
  ExecutionRunsTablePanelDebugPage,
  FeatureDetailPage,
  FeaturesPage,
  JobDetailPage,
  JobRunDetailPage,
  JobsPage,
  LandingPage,
  OnboardingDebugIndexPage,
  OnboardingDebugScreenPage,
  OnboardingPage,
  OrganisationSettingsPage,
  ProjectDetailPage,
  PipelineDetailPage,
  PipelinesPage,
  PlanPage,
  PreviewPage,
  PrivacyRequestsPage,
  ReleaseDetailPage,
  ReleasesPage,
  ReportsPage,
  ResetPasswordConfirmPage,
  ResetPasswordPage,
  ResultsFileDetailPage,
  SessionExpired,
  SignInPage,
  SignUpPage,
  StageDetailPage,
  StageExecutionDetailPage,
  StagesPage,
  SuiteDetailPage,
  SuiteRunDetailPage,
  SuitesPage,
  SuperuserPage,
  TestDetailPage,
  TestRunDetailPage,
  TestsPage,
  UploadDetailPage,
  UserSettingsPage,
  VerifyEmailPage,
} from './routePages';
import { createPolicyPage } from './routePolicyPages';
import type { AuthError, AuthSession } from '@/auth/types';
import type { PageMetadataOptions } from '@/ui/hooks/usePageTitle';
import type { OrganisationDto, ProjectDto } from '@flowqa/shared';
const authBypassClerkEnabled = import.meta.env.VITE_AUTH_BYPASS_CLERK === 'true';
const localDebugRoutesEnabled =
  import.meta.env.DEV &&
  (typeof window === 'undefined' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === '::1');

export type AppRouteContext = {
  session: AuthSession | null;
  authLoading: boolean;
  authError: AuthError | null;
  signIn: (input: { email: string; password: string }) => Promise<void>;
  signUp: (input: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    acceptedPolicies: boolean;
  }) => Promise<void>;
  signOut: () => void;
  refreshSession: () => Promise<boolean>;
  setSessionDirect: (next: AuthSession | null) => void;
  isAdmin: boolean;
  organisations: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  projects: ProjectDto[];
  onAddProject: () => void;
};

export type AppRouteDefinition = {
  key: string;
  path: string;
  enabled?: boolean;
  title?: string;
  description?: string;
  metadata?: PageMetadataOptions;
  featureFlag?: string;
  requiresAuth?: boolean;
  requiresAdmin?: boolean;
  requiresSuperuser?: boolean;
  devOnly?: boolean;
  element: (context: AppRouteContext) => ReactElement;
  nav?: {
    label: string;
    icon: ReactElement;
    requiresAdmin?: boolean;
    requiresSuperuser?: boolean;
    secondary?: string;
    secondaryPath?: string;
    disabled?: boolean;
    secondaryDisabled?: boolean;
  };
};

export const appRouteDefinitions: AppRouteDefinition[] = [
  {
    key: 'landing',
    path: '/',
    title: 'FlowQA · Test Results Analytics and QA Reporting',
    description:
      'FlowQA helps QA and engineering teams track test results over time, spot flaky or recurring failures, and turn raw test reports into quality trends.',
    metadata: { canonicalPath: '/', robots: 'index,follow' },
    element: () => <LandingPage />,
  },
  {
    key: 'preview',
    path: '/preview',
    title: 'Preview',
    description:
      'Review an example FlowQA dashboard with success rate trends, suite history, recurring failures, and QA reporting views.',
    metadata: { canonicalPath: '/preview', robots: 'index,follow' },
    element: () => <PreviewPage />,
  },
  {
    key: 'debug-auth',
    path: '/__debug/auth',
    title: 'Debug: Auth',
    enabled: localDebugRoutesEnabled,
    devOnly: true,
    element: () => <AuthDebugIndexPage />,
  },
  {
    key: 'debug-auth-screen',
    path: '/__debug/auth/:screen',
    title: 'Debug: Auth',
    enabled: localDebugRoutesEnabled,
    devOnly: true,
    element: () => <AuthDebugScreenPage />,
  },
  {
    key: 'debug-onboarding',
    path: '/__debug/onboarding',
    title: 'Debug: Onboarding',
    enabled: localDebugRoutesEnabled,
    devOnly: true,
    element: () => <OnboardingDebugIndexPage />,
  },
  {
    key: 'debug-onboarding-screen',
    path: '/__debug/onboarding/:screen',
    title: 'Debug: Onboarding',
    enabled: localDebugRoutesEnabled,
    devOnly: true,
    element: () => <OnboardingDebugScreenPage />,
  },
  ...publicSiteRouteDefinitions,
  {
    key: 'cookie-policy',
    path: '/cookie-policy',
    title: 'Cookie Policy',
    description: 'Review the FlowQA cookie policy and manage consent preferences.',
    metadata: { canonicalPath: '/cookie-policy', robots: 'index,follow' },
    element: createPolicyPage('07370c92-4ecc-41fe-836e-4487b26a9aa8'),
  },
  {
    key: 'cookie-policy-html',
    path: '/cookie-policy.html',
    title: 'Cookie Policy',
    element: () => <Navigate to="/cookie-policy" replace />,
  },
  {
    key: 'privacy-requests',
    path: '/privacy-requests',
    title: 'Privacy Requests',
    description: 'Submit a FlowQA privacy request for access, deletion, or other data rights.',
    metadata: { canonicalPath: '/privacy-requests', robots: 'noindex,follow' },
    element: () => <PrivacyRequestsPage />,
  },
  {
    key: 'privacy-policy',
    path: '/privacy-policy',
    title: 'Privacy Policy',
    description: 'Review how FlowQA handles privacy, personal data, and user rights.',
    metadata: { canonicalPath: '/privacy-policy', robots: 'index,follow' },
    element: createPolicyPage('9ec55e0f-2e88-41a9-803b-5e1180513847'),
  },
  {
    key: 'privacy-policy-html',
    path: '/privacy-policy.html',
    title: 'Privacy Policy',
    element: () => <Navigate to="/privacy-policy" replace />,
  },
  {
    key: 'terms-of-service',
    path: '/terms-of-service',
    title: 'Terms of Service',
    description: 'Review the FlowQA terms of service for using the product and website.',
    metadata: { canonicalPath: '/terms-of-service', robots: 'index,follow' },
    element: createPolicyPage('6698af93-e6c9-4ffa-8f24-0ff34b3e022d'),
  },
  {
    key: 'acceptable-use-policy',
    path: '/acceptable-use-policy',
    title: 'Acceptable Use Policy',
    description: 'Review the FlowQA acceptable use policy for product and website usage.',
    metadata: { canonicalPath: '/acceptable-use-policy', robots: 'index,follow' },
    element: createPolicyPage('cea0d21a-5c0b-4a92-bd65-7501d1af6e98'),
  },
  {
    key: 'acceptable-use-policy-html',
    path: '/acceptable-use-policy.html',
    title: 'Acceptable Use Policy',
    element: () => <Navigate to="/acceptable-use-policy" replace />,
  },
  {
    key: 'terms-of-service-html',
    path: '/terms-of-service.html',
    title: 'Terms of Service',
    element: () => <Navigate to="/terms-of-service" replace />,
  },
  {
    key: 'sign-in',
    path: `${AUTH_ROUTE_PATHS.signIn}/*`,
    title: 'Sign In',
    element: ({ session, authError, signOut }) => (
      <SignInPage session={session} authError={authError} signOut={signOut} />
    ),
  },
  {
    key: 'sign-up',
    path: `${AUTH_ROUTE_PATHS.signUp}/*`,
    title: 'Sign Up',
    element: ({ session, authLoading, authError, signOut }) => (
      <SignUpPage
        session={session}
        authLoading={authLoading}
        authError={authError}
        signOut={signOut}
      />
    ),
  },
  {
    key: 'auth-callback',
    path: AUTH_ROUTE_PATHS.authCallback,
    title: 'Auth Callback',
    element: () =>
      authBypassClerkEnabled ? <Navigate to="/sign-in" replace /> : <AuthCallbackPage />,
  },
  {
    key: 'accept-invitation',
    path: AUTH_ROUTE_PATHS.acceptInvitation,
    title: 'Accept Invitation',
    element: ({ session, authLoading, authError }) =>
      authBypassClerkEnabled ? (
        <Navigate to="/sign-in" replace />
      ) : (
        <AcceptInvitationPage session={session} authLoading={authLoading} authError={authError} />
      ),
  },
  {
    key: 'reset-password',
    path: AUTH_ROUTE_PATHS.resetPassword,
    title: 'Reset Password',
    element: ({ session }) => <ResetPasswordPage session={session} />,
  },
  {
    key: 'reset-password-confirm',
    path: AUTH_ROUTE_PATHS.resetPasswordConfirm,
    title: 'Choose New Password',
    element: ({ session }) => <ResetPasswordConfirmPage session={session} />,
  },
  {
    key: 'verify-email',
    path: AUTH_ROUTE_PATHS.verifyEmail,
    title: 'Verify Email',
    element: ({ session }) => <VerifyEmailPage session={session} />,
  },
  {
    key: 'onboarding',
    path: '/onboarding',
    title: 'Get Started',
    requiresAuth: true,
    element: ({ session, organisations }) => (
      <OnboardingPage session={session} organisations={organisations} />
    ),
  },
  {
    key: 'reports',
    path: '/reports',
    title: 'Reports',
    requiresAuth: true,
    featureFlag: 'reports',
    element: () => <ReportsPage />,
  },
  {
    key: 'settings',
    path: '/account-settings',
    title: 'Account Settings',
    requiresAuth: true,
    element: ({ session }) => <UserSettingsPage session={session} />,
  },
  {
    key: 'settings-redirect',
    path: '/settings',
    title: 'Account Settings',
    requiresAuth: true,
    element: () => <Navigate to="/account-settings" replace />,
  },
  {
    key: 'plan',
    path: '/plan-settings',
    title: 'Plan Settings',
    requiresAuth: true,
    element: () => <PlanPage />,
  },
  {
    key: 'superuser',
    path: '/superuser',
    title: 'Superuser',
    requiresAuth: true,
    requiresSuperuser: true,
    element: ({ session }) => <SuperuserPage session={session} />,
    nav: { label: 'Superuser', icon: <SettingsIcon fontSize="small" />, requiresSuperuser: true },
  },
  {
    key: 'feature-flags-redirect',
    path: '/feature-flags',
    title: 'Superuser',
    requiresAuth: true,
    requiresSuperuser: true,
    element: () => <Navigate to="/superuser" replace />,
  },
  {
    key: 'projects',
    path: '/projects',
    title: 'Projects',
    requiresAuth: true,
    element: () => <Navigate to="/execution-runs" replace />,
  },
  {
    key: 'releases',
    path: '/releases',
    title: 'Releases',
    requiresAuth: true,
    featureFlag: 'releases',
    element: () => <ReleasesPage />,
    nav: { label: 'Releases', icon: <ReleasesIcon fontSize="small" /> },
  },
  {
    key: 'features',
    path: '/features',
    title: 'Features',
    requiresAuth: true,
    featureFlag: 'features',
    element: () => <FeaturesPage />,
    nav: { label: 'Features', icon: <FeaturesIcon fontSize="small" /> },
  },
  {
    key: 'organisations',
    path: '/organisations/:slug',
    title: 'Workspace',
    requiresAuth: true,
    element: () => <Navigate to="/execution-runs" replace />,
  },
  {
    key: 'organisation-settings-legacy',
    path: '/organisation-settings',
    title: 'Workspace Settings',
    requiresAuth: true,
    element: () => <LegacyWorkspaceSettingsRedirect />,
  },
  {
    key: 'workspace-settings',
    path: '/workspace-settings',
    title: 'Workspace Settings',
    requiresAuth: true,
    element: ({ session }) => <OrganisationSettingsPage session={session} />,
  },
  {
    key: 'project-detail',
    path: '/projects/:slug',
    title: 'Project',
    requiresAuth: true,
    element: ({ session }) => <ProjectDetailPage session={session} />,
  },
  {
    key: 'project-settings',
    path: '/project-settings',
    title: 'Project Settings',
    requiresAuth: true,
    element: ({ session }) => <ProjectSettingsPage session={session} />,
  },
  {
    key: 'workspace-project-settings',
    path: '/workspaces/:workspaceSlug/projects/:projectSlug/settings',
    title: 'Project Settings',
    requiresAuth: true,
    element: ({ session }) => <ProjectSettingsSlugRedirect session={session} />,
  },
  ...codeCoverageRouteDefinitions,
  ...projectScopedProductRouteDefinitions,
  {
    key: 'execution-runs',
    path: '/execution-runs',
    title: 'Execution Runs',
    description:
      'View execution runs, success rates, status trends, and upload outcomes across your project.',
    requiresAuth: true,
    element: () => <ExecutionRunsPage />,
    nav: {
      label: 'Execution Runs',
      icon: <PlayCircleFilledWhiteOutlinedIcon fontSize="small" />,
    },
  },
  {
    key: 'execution-runs-debug-panel',
    path: '/execution-runs/debug/panel',
    title: 'Debug: Execution Runs Table Panel',
    enabled: localDebugRoutesEnabled,
    requiresAuth: true,
    devOnly: true,
    element: () => <ExecutionRunsTablePanelDebugPage />,
  },
  {
    key: 'execution-runs-debug-tables',
    path: '/execution-runs/debug/:tableId',
    title: 'Debug: Execution Runs Tables',
    enabled: localDebugRoutesEnabled,
    requiresAuth: true,
    devOnly: true,
    element: () => <ExecutionRunsTableDebugPage />,
  },
  {
    key: 'uploads-redirect',
    path: '/uploads',
    title: 'Execution Runs',
    requiresAuth: true,
    featureFlag: 'uploads',
    element: () => <Navigate to="/execution-runs" replace />,
  },
  {
    key: 'upload-detail',
    path: '/uploads/:id',
    title: 'Upload',
    requiresAuth: true,
    featureFlag: 'uploads',
    element: ({ session }) => <UploadDetailPage session={session} />,
  },
  {
    key: 'execution-run-detail',
    path: '/execution-runs/:id',
    title: 'Execution Run',
    requiresAuth: true,
    element: ({ session }) => <ExecutionRunDetailPage session={session} />,
  },
  {
    key: 'suites',
    path: '/suites',
    title: 'Suites',
    requiresAuth: true,
    element: () => <SuitesPage />,
    nav: {
      label: 'Suites',
      icon: <SuitesIcon fontSize="small" />,
    },
  },
  {
    key: 'tests',
    path: '/tests',
    title: 'Tests',
    requiresAuth: true,
    element: () => <TestsPage />,
    nav: {
      label: 'Tests',
      icon: <TestsIcon fontSize="small" />,
    },
  },
  {
    key: 'pipelines',
    path: '/pipelines',
    title: 'Pipelines',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: () => <PipelinesPage />,
    nav: {
      label: 'Pipelines',
      icon: <BuildsIcon fontSize="small" />,
    },
  },
  {
    key: 'stages',
    path: '/stages',
    title: 'Stages',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: () => <StagesPage />,
    nav: {
      label: 'Stages',
      icon: <DriveFileMoveIcon fontSize="small" />,
    },
  },
  {
    key: 'jobs',
    path: '/jobs',
    title: 'Jobs',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: () => <JobsPage />,
    nav: {
      label: 'Jobs',
      icon: <StagesIcon fontSize="small" />,
    },
  },
  {
    key: 'stage-run-detail',
    path: '/stages/:stageSlug/runs/:stageExecutionId',
    title: 'Stage Run',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <StageExecutionDetailPage session={session} />,
  },
  {
    key: 'stage-detail',
    path: '/stages/:stageSlug',
    title: 'Stage',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <StageDetailPage session={session} />,
  },
  {
    key: 'job-run-detail',
    path: '/jobs/:jobSlug/runs/:jobRunId',
    title: 'Job Run',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <JobRunDetailPage session={session} />,
  },
  {
    key: 'job-detail',
    path: '/jobs/:jobSlug',
    title: 'Job',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <JobDetailPage session={session} />,
  },
  {
    key: 'stage-run-detail-structured-legacy',
    path: '/pipelines/:pipelineId/stages/:stageName/runs/:stageExecutionId',
    title: 'Stage Run',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <StageExecutionDetailPage session={session} />,
  },
  {
    key: 'stage-detail-structured-legacy',
    path: '/pipelines/:pipelineId/stages/:stageName',
    title: 'Stage',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <StageDetailPage session={session} />,
  },
  {
    key: 'job-run-detail-structured-legacy',
    path: '/pipelines/:pipelineId/jobs/:stageName/:jobName/runs/:jobRunId',
    title: 'Job Run',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <JobRunDetailPage session={session} />,
  },
  {
    key: 'job-detail-structured-legacy',
    path: '/pipelines/:pipelineId/jobs/:stageName/:jobName',
    title: 'Job',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <JobDetailPage session={session} />,
  },
  {
    key: 'job-run-detail-legacy',
    path: '/jobs/runs/:jobRunId',
    title: 'Job Run',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <JobRunDetailPage session={session} />,
  },
  {
    key: 'stage-execution-detail-legacy',
    path: '/stages/executions/:stageExecutionId',
    title: 'Stage Execution',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <StageExecutionDetailPage session={session} />,
  },
  {
    key: 'stage-detail-legacy',
    path: '/stages/legacy/:stageId',
    title: 'Stage',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <StageDetailPage session={session} />,
  },
  {
    key: 'job-detail-legacy',
    path: '/jobs/legacy/:jobId',
    title: 'Job',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <JobDetailPage session={session} />,
  },
  {
    key: 'pipeline-detail',
    path: '/pipelines/:pipelineSlug',
    title: 'Pipeline',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <PipelineDetailPage session={session} />,
  },
  {
    key: 'build-detail',
    path: '/pipelines/:pipelineSlug/runs/:runNumber',
    title: 'Job',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <BuildDetailPage session={session} />,
  },
  {
    key: 'results-files-redirect',
    path: '/results-files',
    title: 'Results Files',
    requiresAuth: true,
    featureFlag: 'uploads',
    element: () => <Navigate to="/execution-runs#results-files" replace />,
  },
  {
    key: 'results-file-detail',
    path: '/results-files/:slug',
    title: 'Results Files',
    requiresAuth: true,
    featureFlag: 'uploads',
    element: ({ session }) => <ResultsFileDetailPage session={session} />,
  },
  {
    key: 'suite-run-detail',
    path: '/suites/:suiteSlug/runs/:runNumber',
    title: 'Suite Run',
    requiresAuth: true,
    element: ({ session }) => <SuiteRunDetailPage session={session} />,
  },
  {
    key: 'test-run-detail',
    path: '/tests/:testSlug/runs/:runNumber',
    title: 'Test Run',
    requiresAuth: true,
    element: ({ session }) => <TestRunDetailPage session={session} />,
  },
  {
    key: 'feature-detail',
    path: '/features/:slug',
    title: 'Feature',
    requiresAuth: true,
    featureFlag: 'features',
    element: ({ session }) => <FeatureDetailPage session={session} />,
  },
  {
    key: 'release-detail',
    path: '/releases/:slug',
    title: 'Release',
    requiresAuth: true,
    featureFlag: 'releases',
    element: ({ session }) => <ReleaseDetailPage session={session} />,
  },
  {
    key: 'suite-detail',
    path: '/suites/:slug',
    title: 'Suite',
    requiresAuth: true,
    element: ({ session }) => <SuiteDetailPage session={session} />,
  },
  {
    key: 'test-detail',
    path: '/tests/:slug',
    title: 'Test',
    requiresAuth: true,
    element: ({ session }) => <TestDetailPage session={session} />,
  },
  {
    key: 'session-expired',
    path: '/session-expired',
    title: 'Session Expired',
    element: () => <SessionExpired />,
  },
];
